/**
 * Utility functions for Google Tag Manager (GTM) dataLayer tracking
 * Target Container: GTM-M8QQKSC3
 */

/**
 * Track begin_checkout event when a user enters checkout or selects a subscription plan.
 * @param {Object} planData
 * @param {number|string} [planData.price] - Price of the subscription/plan
 * @param {string} [planData.currency="USD"] - Currency code (e.g. USD)
 * @param {string} [planData.plan_name] - Name of the selected plan
 * @param {string|number} [planData.subscription_id] - ID of the plan
 */
export const trackBeginCheckout = (planData = {}) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];

  const price = parseFloat(planData.price || planData.subscriptionPrice || 0);
  const currency = planData.currency || "USD";
  const planName = planData.plan_name || planData.name || "Subscription Plan";
  const planId = planData.subscription_id || planData.id || "";

  window.dataLayer.push({
    event: "begin_checkout",
    ecommerce: {
      currency: currency,
      value: price,
      items: [
        {
          item_id: String(planId),
          item_name: planName,
          price: price,
          quantity: 1,
        },
      ],
    },
    value: price,
    currency: currency,
    plan_name: planName,
  });
};

/**
 * Track conversion event on payment success (StartTrial for free trials vs purchase for paid conversions).
 * Prevents duplicate triggers on page refresh using sessionStorage transaction locking.
 * 
 * @param {Object} params
 * @param {boolean} [params.isTrial=false] - Whether this checkout started a free trial
 * @param {string|number} [params.transactionId] - Unique transaction / session / order ID
 * @param {number|string} [params.price=0] - Conversion amount
 * @param {string} [params.currency="USD"] - Currency code
 * @param {string} [params.planName] - Subscription plan name
 * @param {string|number} [params.planId] - Subscription plan ID
 */
export const trackSuccessEvent = ({
  isTrial = false,
  transactionId = "",
  price = 0,
  currency = "USD",
  planName = "Subscription Plan",
  planId = "",
} = {}) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];

  const txnId =
    transactionId ||
    localStorage.getItem("s2s_transaction_id") ||
    localStorage.getItem("selectedSubId") ||
    `txn_${Date.now()}`;

  const storageKey = `gtm_tracked_txn_${txnId}`;

  // Duplicate event protection: skip if transaction was already tracked in current session
  if (sessionStorage.getItem(storageKey)) {
    console.log(`[GTM] Conversion event already tracked for transaction: ${txnId}`);
    return;
  }

  const numericPrice = parseFloat(price || 0);
  const eventName = isTrial ? "StartTrial" : "purchase";

  const eventPayload = {
    event: eventName,
    transaction_id: String(txnId),
    value: numericPrice,
    currency: currency,
    plan_name: planName,
    ecommerce: {
      transaction_id: String(txnId),
      currency: currency,
      value: numericPrice,
      items: [
        {
          item_id: String(planId),
          item_name: planName,
          price: numericPrice,
          quantity: 1,
        },
      ],
    },
  };

  window.dataLayer.push(eventPayload);

  // Mark this transaction as processed to prevent duplicate fires on refresh
  sessionStorage.setItem(storageKey, "true");
};
